// src/pages/SettingsPage.tsx - Updated for projects architecture
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  FormGroup,
  Chip,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  AccountCircle as ProfileIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, deleteUserAccount, updateSubscription } from '../utils/userApi';
import { updateProfile, deleteUser } from 'firebase/auth';

interface UserPreferences {
  emailNotifications: boolean;
  projectNotifications: boolean;
  analysisNotifications: boolean;
}

const SettingsPage: React.FC = () => {
  const { currentUser, userProfile, refreshUserProfile, logout } = useAuth();
  
  // Form states
  const [displayName, setDisplayName] = useState('');
  const [preferences, setPreferences] = useState<UserPreferences>({
    emailNotifications: true,
    projectNotifications: true,
    analysisNotifications: true,
  });
  
  // UI states
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Subscription management state
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);

  // Initialize form data from user profile
  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.display_name || '');
      
      // Merge user preferences with defaults
      const userPrefs = userProfile.preferences || {};
      setPreferences(prev => ({
        ...prev,
        ...userPrefs
      }));
    } else if (currentUser) {
      setDisplayName(currentUser.displayName || '');
    }
  }, [userProfile, currentUser]);

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      // Update Firebase Auth display name if changed
      if (currentUser.displayName !== displayName) {
        await updateProfile(currentUser, { displayName });
      }

      // Update backend profile
      await updateUserProfile({
        display_name: displayName,
        preferences: preferences,
      });

      // Refresh user profile to get latest data
      await refreshUserProfile();

      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (error: any) {
      console.error('Error updating profile:', error);
      setErrorMessage(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    setIsDeleting(true);
    
    try {
      // Step 1: Delete user from our backend/Firestore first
      await deleteUserAccount();
      
      // Step 2: Delete from Firebase Auth
      await deleteUser(currentUser);
      
      // Step 3: Logout (this will redirect to home page)
      await logout();
      
    } catch (error: any) {
      console.error('Error deleting account:', error);
      
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/requires-recent-login') {
        setErrorMessage('For security reasons, please log out and log back in, then try deleting your account again.');
      } else {
        setErrorMessage(error.message || 'Failed to delete account completely. Please contact support.');
      }
      
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePreferenceChange = (key: keyof UserPreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getSubscriptionColor = (tier?: string) => {
    switch (tier) {
      case 'pro':
        return 'warning';
      case 'enterprise':
        return 'error';
      case 'free':
      default:
        return 'default';
    }
  };

  const handleSubscriptionChange = async (newTier: string) => {
    if (!currentUser || newTier === userProfile?.subscription_tier) return;

    setIsUpdatingSubscription(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await updateSubscription(newTier as 'free' | 'pro' | 'enterprise');
      await refreshUserProfile(); // Refresh to get updated limits
      setSuccessMessage(`Subscription updated to ${newTier.toUpperCase()} successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      setErrorMessage(error.message || 'Failed to update subscription');
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  if (!currentUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Account Settings
      </Typography>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              avatar={<ProfileIcon />}
              title="Profile Information"
              action={
                <Button
                  startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                  variant={isEditing ? "contained" : "outlined"}
                  onClick={isEditing ? handleSaveProfile : () => setIsEditing(true)}
                  disabled={isSaving}
                >
                  {isSaving ? <CircularProgress size={20} /> : (isEditing ? 'Save' : 'Edit')}
                </Button>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                {/* Profile Picture - Only show if exists */}
                {(userProfile?.photo_url || currentUser.photoURL) && (
                  <Grid item xs={12} sm={4} display="flex" flexDirection="column" alignItems="center">
                    <Avatar
                      src={userProfile?.photo_url || currentUser.photoURL || undefined}
                      sx={{ width: 120, height: 120, mb: 2 }}
                    >
                      {displayName?.[0]?.toUpperCase() || currentUser.email?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      Profile picture from {currentUser.providerData[0]?.providerId?.replace('.com', '') || 'provider'}
                    </Typography>
                  </Grid>
                )}

                {/* Profile Fields */}
                <Grid item xs={12} sm={(userProfile?.photo_url || currentUser.photoURL) ? 8 : 12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Display Name"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        disabled={!isEditing}
                        variant={isEditing ? "outlined" : "filled"}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={userProfile?.email || currentUser.email || 'N/A'}
                        disabled
                        variant="filled"
                        helperText="Email is managed by your authentication provider"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Subscription Plan</InputLabel>
                        <Select
                          value={userProfile?.subscription_tier || 'free'}
                          onChange={(e) => handleSubscriptionChange(e.target.value)}
                          disabled={isUpdatingSubscription}
                          label="Subscription Plan"
                        >
                          <MenuItem value="free">
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                              <Box>
                                <Typography variant="body1">Free</Typography>
                                <Typography variant="caption" color="text.secondary">1 GB storage</Typography>
                              </Box>
                              {userProfile?.subscription_tier === 'free' && (
                                <Chip label="Current" color="success" size="small" />
                              )}
                            </Box>
                          </MenuItem>
                          <MenuItem value="pro">
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                              <Box>
                                <Typography variant="body1">Pro</Typography>
                                <Typography variant="caption" color="text.secondary">100 GB storage</Typography>
                              </Box>
                              {userProfile?.subscription_tier === 'pro' && (
                                <Chip label="Current" color="warning" size="small" />
                              )}
                            </Box>
                          </MenuItem>
                          <MenuItem value="enterprise">
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                              <Box>
                                <Typography variant="body1">Enterprise</Typography>
                                <Typography variant="caption" color="text.secondary">1 TB storage</Typography>
                              </Box>
                              {userProfile?.subscription_tier === 'enterprise' && (
                                <Chip label="Current" color="error" size="small" />
                              )}
                            </Box>
                          </MenuItem>
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          {isUpdatingSubscription ? 'Updating subscription...' : 'Free switching enabled for testing'}
                        </Typography>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Member Since"
                        value={formatDate(userProfile?.created_at)}
                        disabled
                        variant="filled"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Projects"
                        value={`${userProfile?.project_count || 0} project${userProfile?.project_count !== 1 ? 's' : ''}`}
                        disabled
                        variant="filled"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Authentication Provider"
                        value={currentUser.providerData[0]?.providerId?.replace('.com', '') || 'Email'}
                        disabled
                        variant="filled"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Preferences */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              avatar={<NotificationsIcon />}
              title="Notification Preferences"
            />
            <CardContent>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.emailNotifications}
                      onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                      disabled={!isEditing}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.projectNotifications}
                      onChange={(e) => handlePreferenceChange('projectNotifications', e.target.checked)}
                      disabled={!isEditing}
                    />
                  }
                  label="Project Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.analysisNotifications}
                      onChange={(e) => handlePreferenceChange('analysisNotifications', e.target.checked)}
                      disabled={!isEditing}
                    />
                  }
                  label="Analysis Complete Notifications"
                />
              </FormGroup>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Get notified about project progress, analysis results, and important updates to help you stay on top of your ecological data analysis.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Statistics */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Account Statistics"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                      {userProfile?.project_count || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Projects
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                      {/* Calculate completed projects from userProfile if available */}
                      0
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed Analyses
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h3" color="info.main" sx={{ fontWeight: 700 }}>
                      {userProfile?.created_at ? 
                        Math.floor((new Date().getTime() - new Date(userProfile.created_at).getTime()) / (1000 * 60 * 60 * 24)) 
                        : 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Days Active
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Security */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              avatar={<SecurityIcon />}
              title="Security & Privacy"
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email Verified
                  </Typography>
                  <Chip 
                    label={currentUser.emailVerified ? 'Verified' : 'Not Verified'}
                    color={currentUser.emailVerified ? 'success' : 'warning'}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(userProfile?.created_at)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(userProfile?.updated_at)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account Type
                  </Typography>
                  <Typography variant="body1">
                    {userProfile?.is_anonymous ? 'Anonymous' : 'Registered'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" color="error" gutterBottom>
                    Danger Zone
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Once you delete your account, there is no going back. This will permanently delete 
                    your profile, all projects, notes, tags, timeline data, and any associated analysis results.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete Account
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete your account? This will completely remove:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 2, pl: 2 }}>
            <li>Your login credentials and authentication</li>
            <li>Your profile and account information</li>
            <li>All your projects and associated data</li>
            <li>Project notes, tags, and timeline history</li>
            <li>Analysis results and reports</li>
            <li>Subscription and billing information</li>
          </Box>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
            This action is irreversible. You will need to create a completely new account to use Field Sense AI again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;